import React, { useCallback, useEffect, useState } from "react";
import { CreditCard, Check, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { partnerBillingCheckout, partnerBillingMe, partnerBillingCancel } from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export default function PartnerBilling({ salonSlug, email }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setState(await partnerBillingMe(salonSlug)); } catch { setState({ subscribed: false }); }
  }, [salonSlug]);
  useEffect(() => { if (salonSlug) load(); }, [salonSlug, load]);

  const start = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await partnerBillingCheckout({ salon_slug: salonSlug, email, origin_url: window.location.origin });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start billing.");
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try { await partnerBillingCancel(salonSlug); toast.success("Membership cancelled — no charge."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel."); }
    finally { setBusy(false); }
  };

  const subscribed = state?.subscribed;

  return (
    <section className="mt-12" data-testid="partner-billing">
      <div className="relative overflow-hidden rounded-2xl border border-sand/70 bg-bone p-6 md:p-9">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-mocha"><ShieldCheck size={13} /> Partner membership</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-ink leading-[0.98]">FYLLR Partner</h2>
            <p className="mt-2 font-serif text-2xl text-espresso">$38.50 <span className="font-sans text-sm text-ink/55">AUD / month · inc GST</span></p>
          </div>
          {subscribed && (
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase border border-burgundy text-burgundy px-3 py-1.5 rounded-full" data-testid="partner-billing-status">
              {state.subscription_status === "trialing" ? "Free trial" : "Active"}
            </span>
          )}
        </div>

        <ul className="mt-6 space-y-2.5 max-w-lg">
          {["Card added now — nothing charged before 7 April 2027",
            "Two launch months free when you join before 7 February",
            "Cancel anytime before your first charge — $0",
            "Clients always book free; this is partner-only"].map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm text-ink/70"><Check size={15} className="text-burgundy mt-0.5 shrink-0" />{t}</li>
          ))}
        </ul>

        {subscribed ? (
          <div className="mt-7">
            <p className="text-sm text-ink/70" data-testid="partner-billing-trial-info">
              {state.subscription_status === "trialing"
                ? <>Your free trial runs until <strong>{fmtDate(state.trial_end)}</strong>. First charge of $38.50 that day, then monthly.</>
                : <>Membership active — billed $38.50/month.</>}
            </p>
            <button type="button" onClick={cancel} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full border border-sand px-5 py-2.5 text-xs text-ink/70 hover:border-burgundy hover:text-burgundy transition-colors" data-testid="partner-billing-cancel">
              {busy ? <Loader2 size={13} className="animate-spin" /> : null} Cancel membership
            </button>
          </div>
        ) : (
          <button type="button" onClick={start} disabled={busy} className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-espresso text-bone px-7 py-3.5 text-sm hover:bg-ink disabled:opacity-60 transition-colors" data-testid="partner-billing-start">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Add card & secure early access
          </button>
        )}
      </div>
    </section>
  );
}
